/**
 * Wo man zuletzt war — damit die Anwendung dort wieder aufmacht.
 *
 * Aus dem Betrieb: die Web-App auf dem iPhone nimmt beim Öffnen von sich aus
 * die zuletzt besuchte Seite wieder auf, das iPad beginnt bei der Übersicht.
 * Zwei Geräte, zwei Verhalten, keines davon gewollt. Und weil sich die alte
 * Adresse auf eine gelöschte Reise bezog, stand da „Diese Reise gibt es
 * nicht" — eine Sackgasse gleich beim Öffnen.
 *
 * Gemerkt wird deshalb hier, im Gerät, und nicht auf dem Server: wo man
 * zuletzt war, ist Ansichtssache dieses Geräts. Wer am Telefon in einer Reise
 * schreibt und am Rechner in einer anderen liest, will nicht, dass ihm das
 * eine das andere umstellt.
 *
 * Gemerkt werden **Chats und die Übersicht**, sonst nichts. Wer zuletzt die
 * Liste offen hatte, soll sie wiedersehen; wer im Chat war, den Chat.
 * Einstellungen, Reiseseite und Formulare gehören nicht dazu — nach einem
 * Neustart in „Reise anlegen" zu landen wäre keine Hilfe, und die
 * Einstellungen sind kein Ort, an dem man wohnt.
 */
const MARKE = 'tb.letzter-chat'

/**
 * Die Wege, an denen man sich aufhält: der Reise-Chat, ein Antwortverlauf --
 * und die Karte. Sie ist eine zweite Ansicht derselben Reise, kein Formular:
 * wer sie offen hatte, will sie wiedersehen.
 */
const CHAT = /^\/t\/\d+(\/with\/\d+|\/karte)?$/

export function istChat(pfad: string): boolean {
  return CHAT.test(pfad)
}

/**
 * Wohin beim Start — gelesen **beim Laden des Moduls**, also vor dem ersten
 * Rendern.
 *
 * Der Zeitpunkt ist keine Feinheit, sondern der Kern: `Shell` schreibt den Ort
 * in einem Effekt mit, und Effekte laufen nach dem Rendern. Ohne diese frühe
 * Kopie hinge das Ergebnis daran, wer zuerst drankommt — und beim Neuladen
 * durch die `StandWache` hätte die verworfene erste Runde bereits „Übersicht"
 * hinterlassen. Genau so kam auf dem iPhone die Liste, obwohl zuletzt ein Chat
 * offen war.
 */
let beimStart = lies()

export type Startziel = { art: 'chat'; pfad: string } | { art: 'liste' } | null

function lies(): Startziel {
  try {
    const pfad = localStorage.getItem(MARKE)
    // Geprüft wird auch beim Lesen: was hier steht, geht ungefragt in die
    // Adresszeile, und ein alter oder verbogener Eintrag hat dort nichts zu
    // suchen.
    if (pfad !== null && istChat(pfad)) return { art: 'chat', pfad }
    if (pfad === '/') return { art: 'liste' }
    return null
  } catch {
    return null
  }
}

export function startziel(): Startziel {
  return beimStart
}

/**
 * Whether the start of this session is still ahead -- the one moment the
 * remembered chat may open on its own.
 *
 * **"Once per session" means the first route of the session, not the first
 * visit to the overview** (1.84.1). From use: "a tap on the suitcase does
 * not always lead back to the trip list at once … not reproducible." The
 * mark lived in `StartPane` and was spent only when `StartPane` was first
 * drawn. A session that starts anywhere else -- iOS resuming the web app at
 * its last address, a notification opening `/t/8?m=…`, the `StandWache`
 * reloading in a chat -- never drew it, so the first tap on the suitcase
 * landed on `/`, looked like the start, and bounced back into the chat.
 * The second tap worked, which is exactly "not at once".
 *
 * `Shell` spends the mark on its first route, whatever the path; `StartPane`
 * only reads it. Module state, not device state: it is this session's.
 */
let startVorbei = false

export function startOffen(): boolean {
  return !startVorbei
}

export function startErledigt(): void {
  startVorbei = true
}

/**
 * Was **jetzt** im Speicher steht — nicht, was beim Öffnen dort stand.
 *
 * Nur für die Standzeile unter den Einstellungen, und dort mit Bedacht: fallen
 * die beiden auseinander, ist beim Öffnen etwas anderes gelesen worden als
 * inzwischen geschrieben wurde. Stehen beide auf „nichts", kommt das Schreiben
 * gar nicht durch. Aus dem Betrieb ist genau das offen — am iPhone wird nichts
 * behalten, am iPad schon — und von außen sind die beiden Fälle nicht zu
 * unterscheiden.
 */
export function gemerkterOrt(): Startziel {
  return lies()
}

export function merkeOrt(pfad: string): void {
  if (pfad !== '/' && !istChat(pfad)) return
  try {
    localStorage.setItem(MARKE, pfad)
  } catch {
    // Nicht merken zu können ist kein Grund, nicht zu bedienen.
  }
}

/**
 * Vergessen — und zwar beides, den Eintrag und die frühe Kopie.
 *
 * Gebraucht beim Abmelden und dann, wenn die gemerkte Reise verschwunden ist.
 * Bliebe die Kopie stehen, schickte sie einen geradewegs dorthin zurück, wo es
 * nichts mehr gibt.
 */
export function vergissOrt(): void {
  beimStart = null
  try {
    localStorage.removeItem(MARKE)
  } catch {
    // Siehe oben.
  }
}
